import get from 'lodash.get';

import type { LinkQueryResult } from '@/components/traceInspector/traceInspector.types';

export * from './actions';
export { dataIdFromObject, idWithType } from './cache';
// export your utils here
export { formatDate, formatDateWithoutSeconds, getNow } from './dates';
export { getFileExt, getFileIconStr } from './fileTypes';
export { default as graphqlWithFilters } from './graphqlWithFilters';
export * from './localStorage';
export * from './numberFormat';
export * from './optimisticResponseHelper';
export { default as randomId } from './randomId';
export * from './sorter';
export * from './sorting';
export * from './strings';
export { checkCommentExists, isJSON } from './textCheck';
export * from './widgets';

export const deepGet = get;

// copies the public key to the clipboard
// see: https://stackoverflow.com/a/30810322
export const addTextToClipBoard = text => {
  const textArea = document.createElement('textarea');
  textArea.style.width = '0';
  textArea.style.height = '0';
  textArea.style.opacity = '0';
  textArea.value = text;
  document.body.appendChild(textArea);
  textArea.select();
  document.execCommand('copy');
  document.body.removeChild(textArea);
};

/**
 Convert a blob into a string.
 */
export const blobToString = blob =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      resolve(reader.result);
    };
    reader.onerror = reject;
    reader.readAsText(blob);
  });

/**
 Save a blob locally.
 */
export const saveBlob = (data, filename) => {
  // save the archive locally
  const url = window.URL.createObjectURL(new Blob([data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  link.click();
};

/**
 Deep copy data.
 */
export const deepCopy = <T>(obj: T): T => JSON.parse(JSON.stringify(obj));

/**
 * Validate url
 */
const pattern = new RegExp(
  '^(https?:\\/\\/)?' +
    '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' +
    '((\\d{1,3}\\.){3}\\d{1,3}))' +
    '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' +
    '(\\?[;&a-z\\d%_.~+=-]*)?' +
    '(\\#[-a-z\\d_]*)?$',
  'i'
);
export const isValidURL = str => !!pattern.test(str);

/**
 * Get the size of the scrollbar in the current browser
 */
export const getScrollbarSize = () => {
  const scrollDiv = document.createElement('div');
  scrollDiv.style.position = 'absolute';
  scrollDiv.style.top = '-9999px';
  scrollDiv.style.width = '50px';
  scrollDiv.style.height = '50px';
  scrollDiv.style.overflow = 'scroll';
  document.body.appendChild(scrollDiv);
  const size = scrollDiv.offsetWidth - scrollDiv.clientWidth;
  document.body.removeChild(scrollDiv);
  return size;
};

/**
 * Function to round a number with an specific number of digits
 * @param {number} number The number that needs to be rounded
 * @param {number} digits The (integer) number of digits expected after the decimal point
 * @returns {number} The rounded number
 */
export const roundNumber = (number: number, digits = 0) => {
  const digitsMultiplier = 10 ** Number(digits);
  const roundedNumber =
    Math.round(number * digitsMultiplier) / digitsMultiplier;
  return roundedNumber;
};

/**
 * Finds the number of files in the `state.data` object.
 * A file is any object which has the property `digest`.
 * */
export const findNumberOfFiles = (data: LinkQueryResult['data']) => {
  try {
    if (typeof data !== 'object') return 0;

    const allArrays = Object.values(data)?.filter(Array.isArray);
    const objectsWithDigest = allArrays.flatMap(array =>
      array.filter(item => typeof item === 'object' && 'digest' in item)
    );

    return objectsWithDigest.length;
  } catch {
    return 0;
  }
};
